import React from 'react';
import { useTheme } from 'styled-components';
import colors from '../../helpers/colors';
import { applyComponentTheme } from '../../theme';

export type SoundCloudPlayerProps = {
  /** Displays the number of times the track/podcast has been played. */
  playcount?: boolean;
  /** Determines whether a 'Share' buttons appears on the player. */
  shareable?: boolean;
  /** Determines whether the associated artwork is displayed on the player. */
  showArtwork?: boolean;
  /**
   * URL of a podcast or track from SoundCloud. Only the suffix of the URL is passed in
   * as a prop, with the rest of the URL provided in the code. This way only a valid
   * SoundCloud URL can be used.
   */
  url: string;
};

/**
 * A SoundCloud Player component that will display an interactive audio player using a
 * widget provided by SoundCloud. The design/appearance of the widget is handled by a
 * SoundCloud iframe with few properties to customise. The appropriate application's
 * primary colour is passed in to the URL to customise the colour of the play/pause
 * button.
 *
 * Only the text after 'soundcloud.com/' needs to be passed in to the URL prop. For
 * example, with the following SoundCloud URL:
 * https://soundcloud.com/hannahlaingdj/good-love. To use this SoundCloud track, you
 * should use 'hannahlaingdj/good-love' for the URL prop. Or with the following URL:
 * https://soundcloud.com/user-582498544-634498785/international-womens-day-2023-rsa-podcast
 * To use this URL, you should use
 * 'user-582498544-634498785/international-womens-day-2023-rsa-podcast'.
 *
 * Documentation on the widget can be found on the SoundCloud website at the link below:
 * https://developers.soundcloud.com/docs/api/html5-widget
 */
const SoundCloudPlayer: React.FC<SoundCloudPlayerProps> = ({
  playcount,
  shareable,
  showArtwork,
  url,
}) => {
  const theme = useTheme();

  const colour = colors.core01({ theme });
  const sanitisedColour = colour.substring(1);

  const baseUrl = `https://w.soundcloud.com/player/?url=https://soundcloud.com/${url}`;
  const urlParams = `&color=${sanitisedColour}&show_playcount=${
    playcount ?? false
  }&buying=false&sharing=${shareable ?? true}&show_artwork=${showArtwork ?? true}`;

  const widgetUrl = `${baseUrl}${urlParams}`;

  return (
    <iframe
      title={`SoundCloud Player-${url}`}
      width="100%"
      height="166"
      src={widgetUrl}
    />
  );
};

export default applyComponentTheme(SoundCloudPlayer, 'SoundCloudPlayer');
